/**
 * Scroll reveals. Add a pattern to GROUPS — that's the whole API.
 *
 * root:       container selector
 * items:      children to animate (omit to animate the root itself)
 * step:       stagger override in ms (defaults to --reveal-stagger in CSS)
 * byRow:      group items by visual row
 * rowTogether: with byRow, fade the whole row at once (no horizontal stagger)
 * slideMedia: media-text — text first, image slides in from its side
 * quoteCover: cover with only a quote — image first, then the quote
 *
 * First-paint hide list lives in _reveal.sass ($reveal-targets) — keep in sync.
 */
(function () {
	const GROUPS = [
		{ root: '.wp-block-columns', items: ':scope > .wp-block-column' },
		{
			root: '.item-cards:not(.is-style-carousel)',
			items: ':scope > li',
			byRow: true,
			rowTogether: true,
		},
		{ root: '.is-style-carousel', step: 0 },
		{
			root: '.wp-block-gallery',
			items: ':scope > .wp-block-image, :scope > .blocks-gallery-item',
			byRow: true,
		},
		{
			root: '.wp-block-cover:has(> .wp-block-cover__inner-container > :only-child.wp-block-quote)',
			items: ':scope > .wp-block-cover__image-background, :scope > .wp-block-cover__video-background, :scope > .wp-block-cover__inner-container',
			quoteCover: true,
		},
		{
			root: '.wp-block-media-text:not(.is-style-circular)',
			items: ':scope > .wp-block-media-text__content, :scope > .wp-block-media-text__media',
			step: 180,
			slideMedia: true,
		},
		{ root: '.is-style-icon-markers', items: ':scope > li' },
		{ root: '.is-layout-grid', items: ':scope > *', byRow: true },
		{ root: '.stats-grid', items: ':scope > .stat-item', byRow: true },
		{ root: '.stats', items: ':scope > .stat', byRow: true },
	];

	const ROW_BAND = 40;
	const pending = new WeakMap();
	const membership = new WeakMap();
	const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
	let observer;
	let focusBound = false;

	function reducedMotion() {
		return motionQuery.matches;
	}

	function cssTimeMs(name, fallback) {
		const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
		const n = parseFloat(raw);

		if (!Number.isFinite(n)) {
			return fallback;
		}

		return /ms$/i.test(raw) ? n : n * 1000;
	}

	function stepOf(group, stagger) {
		return group.step == null ? stagger : group.step;
	}

	function split(selector) {
		return selector.split(',').map(function (part) {
			return part.trim();
		});
	}

	function containsNestedGroup(el) {
		for (let i = 0; i < GROUPS.length; i++) {
			const parts = split(GROUPS[i].root);
			for (let j = 0; j < parts.length; j++) {
				if (el.matches(parts[j]) || el.querySelector(parts[j])) {
					return true;
				}
			}
		}
		return false;
	}

	function inViewport(el) {
		const rect = el.getBoundingClientRect();
		const vh = window.innerHeight || document.documentElement.clientHeight;
		const vw = window.innerWidth || document.documentElement.clientWidth;

		return rect.bottom > 0 && rect.right > 0 && rect.top < vh && rect.left < vw;
	}

	function markItem(el, delay, extraClass) {
		el.classList.add('js-reveal');
		if (extraClass) {
			el.classList.add(extraClass);
		}
		el.style.setProperty('--reveal-delay', delay + 'ms');
	}

	function show(items) {
		items.forEach(function (el) {
			el.classList.add('is-in');
		});
	}

	function remember(lead, items) {
		const group = { lead: lead, items: items };
		membership.set(lead, group);
		items.forEach(function (el) {
			membership.set(el, group);
		});
		return group;
	}

	function revealGroup(group) {
		if (!group) {
			return;
		}

		if (observer) {
			observer.unobserve(group.lead);
		}

		pending.delete(group.lead);
		show(group.items);
	}

	function observe(lead, items) {
		remember(lead, items);
		pending.set(lead, items);
		observer.observe(lead);
	}

	function revealOrObserve(lead, items) {
		const waiting = [];

		items.forEach(function (el) {
			if (inViewport(el)) {
				el.classList.add('is-in');
			} else {
				waiting.push(el);
			}
		});

		if (!waiting.length) {
			return;
		}

		if (waiting.length === items.length) {
			observe(lead, waiting);
			return;
		}

		waiting.forEach(function (el) {
			observe(el, [el]);
		});
	}

	function showSkipped(root, group) {
		const raw = group.items
			? Array.prototype.slice.call(root.querySelectorAll(group.items))
			: [root];

		raw.forEach(function (el) {
			el.classList.add('is-in');
		});
		root.classList.add('js-reveal-bound');
	}

	function rowsOf(items) {
		const tops = items.map(function (el) {
			return el.offsetTop;
		});
		const rows = [];

		items.forEach(function (el, i) {
			const y = tops[i];
			let row = rows.find(function (entry) {
				return Math.abs(entry.y - y) < ROW_BAND;
			});

			if (!row) {
				row = { y: y, items: [] };
				rows.push(row);
			}

			row.items.push(el);
		});

		return rows;
	}

	function prepare(root, group, hero, stagger) {
		if (root.classList.contains('js-reveal-bound')) {
			return;
		}

		if (hero && hero.contains(root) && !group.quoteCover) {
			showSkipped(root, group);
			return;
		}

		if (root.closest('.main-footer')) {
			showSkipped(root, group);
			return;
		}

		const raw = group.items
			? Array.prototype.slice.call(root.querySelectorAll(group.items))
			: [root];
		const skipped = [];
		const items = raw.filter(function (el) {
			if (el.classList.contains('js-reveal') || el.classList.contains('is-in')) {
				return false;
			}

			// CSS hides these; if we don't animate them, they must still become visible.
			if (group.items && containsNestedGroup(el)) {
				skipped.push(el);
				return false;
			}

			return true;
		});

		skipped.forEach(function (el) {
			el.classList.add('is-in');
		});

		if (!items.length) {
			root.classList.add('js-reveal-bound');
			return;
		}

		if (group.quoteCover) {
			const hasMedia = items.some(function (el) {
				return !el.classList.contains('wp-block-cover__inner-container');
			});

			if (!hasMedia) {
				items.forEach(function (el) {
					el.classList.add('is-in');
				});
				root.classList.add('js-reveal-bound');
				return;
			}
		}

		root.classList.add('js-reveal-bound');

		const step = stepOf(group, stagger);

		if (group.slideMedia) {
			const fromRight = root.classList.contains('has-media-on-the-right');
			items.forEach(function (el) {
				const isMedia = el.classList.contains('wp-block-media-text__media');
				markItem(
					el,
					isMedia ? step : 0,
					isMedia ? (fromRight ? 'js-reveal--from-right' : 'js-reveal--from-left') : '',
				);
			});
			revealOrObserve(root, items);
			return;
		}

		if (group.quoteCover) {
			items.forEach(function (el) {
				const isContent = el.classList.contains('wp-block-cover__inner-container');
				markItem(el, isContent ? step : 0);
			});
			revealOrObserve(root, items);
			return;
		}

		if (group.byRow) {
			rowsOf(items).forEach(function (row) {
				row.items.forEach(function (el, i) {
					markItem(el, group.rowTogether ? 0 : i * step);
				});
				revealOrObserve(row.items[0], row.items);
			});
			return;
		}

		items.forEach(function (el, i) {
			markItem(el, i * step);
		});
		revealOrObserve(root, items);
	}

	function showOrphans() {
		GROUPS.forEach(function (group) {
			document.querySelectorAll(group.root).forEach(function (root) {
				const raw = group.items
					? Array.prototype.slice.call(root.querySelectorAll(group.items))
					: [root];

				raw.forEach(function (el) {
					if (!el.classList.contains('js-reveal') && !el.classList.contains('is-in')) {
						el.classList.add('is-in');
					}
				});
			});
		});
	}

	function disableReveals() {
		if (observer) {
			observer.disconnect();
			observer = null;
		}

		document.documentElement.classList.add('js-reveal-fail');
		document.querySelectorAll('.js-reveal').forEach(function (el) {
			el.classList.add('is-in');
		});
	}

	function onFocusIn(event) {
		const el = event.target.closest && event.target.closest('.js-reveal');
		if (!el || el.classList.contains('is-in')) {
			return;
		}

		revealGroup(membership.get(el) || { lead: el, items: [el] });
	}

	function runReveal() {
		window.clearTimeout(window.__revealFailsafe);

		try {
			if (reducedMotion()) {
				disableReveals();
				return;
			}

			if (!observer) {
				observer = new IntersectionObserver(
					function (entries) {
						entries.forEach(function (entry) {
							if (!entry.isIntersecting) {
								return;
							}

							revealGroup(
								membership.get(entry.target) || {
									lead: entry.target,
									items: pending.get(entry.target) || [entry.target],
								},
							);
						});
					},
					{ threshold: 0.01, rootMargin: '0px 0px -8% 0px' },
				);
			}

			if (!focusBound) {
				document.addEventListener('focusin', onFocusIn);
				focusBound = true;
			}

			const stagger = cssTimeMs('--reveal-stagger', 120);
			const hero = document.querySelector('.entry-content .wp-block-cover');

			GROUPS.forEach(function (group) {
				document.querySelectorAll(group.root).forEach(function (root) {
					prepare(root, group, hero, stagger);
				});
			});

			showOrphans();
		} catch (err) {
			document.documentElement.classList.add('js-reveal-fail');
		}
	}

	window.runReveal = runReveal;

	if (typeof motionQuery.addEventListener === 'function') {
		motionQuery.addEventListener('change', function (event) {
			if (event.matches) {
				disableReveals();
			}
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', runReveal);
	} else {
		runReveal();
	}
})();
